// Shared statement/block-scoped procedural parse (plan16 T4).
//
// Generalizes the mysql DELIMITER slicing precedent (mysqlProceduralLocals.ts)
// into ONE helper for the three renderer-side procedural dialects — plsql,
// plpgsql, mysql-routines. Instead of parsing the whole buffer on every
// completion request (measured ~5 s warm / ~12 s cold on a 67 KB file), each
// dialect supplies a `sliceFor` that returns the enclosing statement range;
// this helper parses ONLY that slice and caches the result keyed by the slice
// TEXT, so repeat completions on an unchanged buffer never re-parse. A 200 ms
// stale-reuse window (same numeric posture as the worker's
// FULL_FILE_RATE_LIMIT_MS, but source-identity-gated — see STALE_REUSE_MS)
// dedups caret movement over an unchanged document, and oversized slices are
// refused (D6).
//
// OFFSET TRANSLATION (the load-bearing contract): the helper returns the
// ABSOLUTE `sliceStart`. The caller computes `offset - sliceStart` when it calls
// its dialect's `resolveScope*` and when it compares the caret against any
// slice-relative `visibleAt` range. All coordinates are UTF-16 code-unit
// offsets — `String.prototype.slice` and Monaco offsets agree — so multi-byte
// content needs no special handling.
//
// A cached body is POSITION-INDEPENDENT: parsing "BEGIN v END" yields the same
// slice-relative offsets no matter where that text sits in the document, so the
// cache is keyed by slice text alone and `sliceStart` always reflects the
// CURRENT request's slice position (never the cached one). `{ body, sliceStart }`
// always travel together as one coherent pair, including on stale-reuse — the
// caller can never receive a body from one slice with a start from another.
//
// Degradation-safe: an oversized slice returns null (caller skips procedural
// locals for the request); a `parse` rejection propagates to the caller's own
// try/catch (every emit* helper degrades to no locals) and is NOT cached.
//
// IN-FLIGHT DEDUP (plan16 T5): two callers for the SAME dialect+slice-text
// key share ONE underlying `parse` call even when both are issued before
// either resolves (e.g. `Promise.all([scopedProceduralParse(a), ...(a)])`).
// This falls out of `bodyCache` holding the `parse()` PROMISE (not its
// resolved value) — the second caller's `state.bodyCache.get(sliceText)`
// lookup finds the first caller's in-flight promise and attaches to it. The
// load-bearing invariant that makes this correct without any explicit
// "in-flight" bookkeeping: nothing in this function `await`s before the
// promise is written into `bodyCache`, so the ENTIRE synchronous portion of
// one call (through the cache write) always completes before a
// back-to-back second call begins running (JS functions run synchronously up
// to their first `await`). If a future edit inserts an `await` earlier in
// this function, that invariant — and this dedup — breaks silently; the
// concurrent-call tests in `scopedProceduralParse.test.ts` are the tripwire.
// A rejected in-flight promise rejects every attached caller (the shared
// promise's rejection propagates through each caller's own `.then`/await) AND
// evicts the cache entry (see the `bodyPromise.catch` below) so the slot is
// never left poisoned for the next request.

import { noteCompletionIoWait } from '@/features/intellisense/telemetry/providerCost';

import { MAX_MAIN_THREAD_SLICE_BYTES } from './proceduralParseLimits';

export { MAX_MAIN_THREAD_SLICE_BYTES };

/** CARET-movement dedup window over an UNCHANGED document. Within this window
 *  a request for a DIFFERENT slice of the byte-identical source reuses the
 *  most-recent parse (stale-ok, spec case 4) rather than paying another. Be
 *  precise about what this is NOT: every edit changes the source, so this
 *  window never fires across keystrokes of a typing storm — an edited
 *  statement always re-slices and re-parses (statement-scale cost, the
 *  envelope D5 accepts). The only firing case is the caret jumping between
 *  statements of an unchanged buffer within 200 ms (the number mirrors the
 *  worker's FULL_FILE_RATE_LIMIT_MS). The source-identity gate is what keeps
 *  reuse inside one document (and keeps the sequential resolver tests that
 *  share this module's state from contaminating each other).
 *
 *  Follow-up (worker parity, relevant to T5's dedup layer): a structural-
 *  signature gate à la `parseWorkerInvalidation.shouldInvalidateBoundaries`
 *  would extend reuse across edits the way the worker does; not built here. */
const STALE_REUSE_MS = 200;

/** Per-dialect LRU cap (D5). Four is enough to cover a caret bouncing between a
 *  couple of routines without unbounded growth. */
const LRU_MAX = 4;

export type ProceduralDialect = 'plsql' | 'plpgsql' | 'mysql-routines';

export interface ScopedParseResult<TBody> {
  readonly body: TBody;
  /** Absolute UTF-16 offset where the parsed slice begins in `source`. The
   *  caller translates the caret with `offset - sliceStart`. */
  readonly sliceStart: number;
}

export interface ScopedProceduralParseArgs<TBody> {
  readonly dialect: ProceduralDialect;
  readonly source: string;
  readonly offset: number;
  /** Returns the enclosing slice `[start, end)` or null → parse the full
   *  source (a dialect that cannot reliably extract a statement, e.g. oracle).
   */
  readonly sliceFor: (source: string, offset: number) => { start: number; end: number } | null;
  /** Parse a slice's text into the dialect body. May re-terminate/transform the
   *  text (mysql appends `;`); the cache key is the RAW slice text, so the
   *  transform must be a pure function of it. */
  readonly parse: (sliceText: string) => Promise<TBody>;
}

interface DialectState {
  /** slice text → parsed body promise (position-independent). LRU by Map
   *  insertion order. */
  readonly bodyCache: Map<string, Promise<unknown>>;
  /** Source of the most-recent fresh parse — stale-reuse only fires when the
   *  incoming source is byte-identical (keeps reuse inside one document). */
  lastSource: string | null;
  /** Coherent `{ body, sliceStart }` of the most-recent fresh parse. */
  lastResult: Promise<ScopedParseResult<unknown>> | null;
  lastParseAt: number;
  /** A fresh parse has resolved, so the dialect's parser classes are loaded
   *  and a new parse runs in the calling task (provider-cost measurement). */
  parserReady: boolean;
}

const states = new Map<ProceduralDialect, DialectState>();

function getState(dialect: ProceduralDialect): DialectState {
  let s = states.get(dialect);
  if (!s) {
    s = { bodyCache: new Map(), lastSource: null, lastResult: null, lastParseAt: 0, parserReady: false };
    states.set(dialect, s);
  }
  return s;
}

// Provider-cost measurement (`telemetry/providerCost.ts`): awaiting a parse
// or result promise that has NOT settled waits on another task — a parse some
// other request started, possibly still behind the parser classes' first
// load — so it is noted as a real-I/O wait; a settled one continues in the
// calling task and stays measurable.
const settledPromises = new WeakSet<Promise<unknown>>();

function trackSettlement(p: Promise<unknown>): void {
  const mark = () => {
    settledPromises.add(p);
  };
  p.then(mark, mark);
}

function noteWaitUnlessSettled(p: Promise<unknown>): void {
  if (!settledPromises.has(p)) noteCompletionIoWait();
}

let clockNow: () => number = defaultNow;

function defaultNow(): number {
  const g = globalThis as { performance?: { now(): number } };
  return g.performance ? g.performance.now() : Date.now();
}

function touchLru(cache: Map<string, Promise<unknown>>, key: string, val: Promise<unknown>): void {
  // Re-insert to move the key to the most-recently-used end.
  cache.delete(key);
  cache.set(key, val);
}

function evictLru(cache: Map<string, Promise<unknown>>): void {
  while (cache.size > LRU_MAX) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

export async function scopedProceduralParse<TBody>(
  args: ScopedProceduralParseArgs<TBody>,
): Promise<ScopedParseResult<TBody> | null> {
  const { dialect, source, offset, sliceFor, parse } = args;

  const range = sliceFor(source, offset) ?? { start: 0, end: source.length };
  const start = Math.max(0, Math.min(range.start, source.length));
  const end = Math.max(start, Math.min(range.end, source.length));
  if (end - start > MAX_MAIN_THREAD_SLICE_BYTES) return null;

  const sliceText = source.slice(start, end);
  const state = getState(dialect);

  // Same-slice body cache — cases (1) (2) (3b). The body is position-
  // independent; `sliceStart` is the CURRENT request's start, so a body cached
  // for one position stays correct when reused at another.
  const cachedBody = state.bodyCache.get(sliceText);
  if (cachedBody) {
    touchLru(state.bodyCache, sliceText, cachedBody);
    noteWaitUnlessSettled(cachedBody);
    return cachedBody.then((body) => ({ body: body as TBody, sliceStart: start }));
  }

  // Stale-reuse — case (4): caret-movement dedup over an UNCHANGED document.
  // Byte-identical source inside the window ⇒ reuse the prior coherent
  // { body, sliceStart } pair rather than pay another parse. Edits never take
  // this path (any edit changes `source`); the cache hit above already
  // answered any previously-seen slice, so this fires only when the caret
  // jumps to a genuinely NEW statement of the same unchanged buffer.
  if (
    state.lastResult &&
    state.lastSource === source &&
    clockNow() - state.lastParseAt < STALE_REUSE_MS
  ) {
    noteWaitUnlessSettled(state.lastResult);
    return state.lastResult as Promise<ScopedParseResult<TBody>>;
  }

  // Fresh parse. Cache the BODY promise (so concurrent same-slice callers share
  // one in-flight parse — the dedup seam T5 builds on) and record the coherent
  // result as the stale-reuse anchor.
  // Until one parse of this dialect has resolved, a parse may wait on the
  // parser classes' first load (a lazy import inside the grammar module).
  if (!state.parserReady) noteCompletionIoWait();
  const bodyPromise = parse(sliceText);
  state.bodyCache.set(sliceText, bodyPromise);
  evictLru(state.bodyCache);
  trackSettlement(bodyPromise);
  void bodyPromise.then(
    () => {
      state.parserReady = true;
    },
    () => undefined,
  );
  const resultPromise: Promise<ScopedParseResult<TBody>> = bodyPromise.then((body) => ({
    body: body as TBody,
    sliceStart: start,
  }));
  trackSettlement(resultPromise);
  state.lastSource = source;
  state.lastResult = resultPromise as Promise<ScopedParseResult<unknown>>;
  state.lastParseAt = clockNow();

  // A rejected parse must NOT poison the cache — drop it so a later keystroke
  // (e.g. after the ANTLR bundle finishes loading) can re-parse.
  void bodyPromise.catch(() => {
    if (state.bodyCache.get(sliceText) === bodyPromise) state.bodyCache.delete(sliceText);
    if (state.lastResult === resultPromise) {
      state.lastResult = null;
      state.lastSource = null;
    }
  });

  return resultPromise;
}

// ─── Test seams ──────────────────────────────────────────────────────────────

/** Inject a deterministic clock for the stale-reuse window (case 4/6). Pass
 *  null to restore the default `performance.now()` / `Date.now()`. */
export function __setScopedParseClockForTest(fn: (() => number) | null): void {
  clockNow = fn ?? defaultNow;
}

/** Clear all per-dialect caches + restore the default clock between tests. */
export function __resetScopedProceduralParseForTest(): void {
  states.clear();
  clockNow = defaultNow;
}

/** Current cache entry count for a dialect — LRU-cap assertions only. */
export function __scopedParseCacheSizeForTest(dialect: ProceduralDialect): number {
  return states.get(dialect)?.bodyCache.size ?? 0;
}
